/**
 * MobileInactiveTeamsModal
 *
 * Mobile full-screen MUI Dialog replacement for the desktop InactiveTeamsModel.
 * Displays inactive teams as a card-based searchable list with reactivation actions.
 * Slide-up transition, sticky AppBar with close button, search field.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Slide,
  Fade,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GroupOffOutlinedIcon from "@mui/icons-material/GroupRemoveOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getTeams,
  getInActiveTeams,
  changeTeamActiveStatus,
  setInactiveTeams,
} from "../../../components/teams/teamsSlice";
import { Localize } from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const cardSx = {
  mx: 1.5,
  mb: 1,
  borderRadius: "18px",
  backgroundColor: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(20px) saturate(1.3)",
  WebkitBackdropFilter: "blur(20px) saturate(1.3)",
  border: "1px solid rgba(255,144,21,0.06)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
  transition: TRANSITION_SMOOTH,
  "&:active": {
    transform: "scale(0.98)",
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param {Object}   props
 * @param {boolean}  props.visible    - controls dialog open state
 * @param {Function} props.setVisible - toggle visibility
 */
export default function MobileInactiveTeamsModal({ visible, setVisible }) {
  const dispatch = useDispatch();
  const inactiveTeams = useSelector(
    (state) => state.teams.inactiveTeams || []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [activatingId, setActivatingId] = useState(null);

  // Fetch inactive teams when opened
  useEffect(() => {
    if (visible) {
      dispatch(getInActiveTeams());
    }
  }, [visible, dispatch]);

  const handleClose = () => {
    setVisible(false);
    dispatch(setInactiveTeams([]));
    setGlobalFilter("");
    setActivatingId(null);
  };

  const activateTeam = async (idTeam) => {
    if (!idTeam) return;
    try {
      setActivatingId(idTeam);
      await dispatch(changeTeamActiveStatus({ id_team: idTeam, active: 1 }));
      dispatch(getInActiveTeams());
      dispatch(getTeams());
    } finally {
      setActivatingId(null);
    }
  };

  // Filter teams based on search
  const filteredTeams = useMemo(() => {
    if (!globalFilter.trim()) return inactiveTeams;
    const query = globalFilter.toLowerCase();
    return inactiveTeams.filter(
      (team) =>
        (team.name && team.name.toLowerCase().includes(query)) ||
        (team.id && String(team.id).includes(query))
    );
  }, [inactiveTeams, globalFilter]);

  const renderTeamCard = (team, index) => (
    <Fade in timeout={200 + index * 60} key={team.id}>
      <Card sx={cardSx} variant="outlined">
        <CardContent
          sx={{
            p: 1.5,
            "&:last-child": { pb: 1.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          {/* Team info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.92rem",
                color: "rgba(0,0,0,0.87)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {team.name || "—"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: "rgba(0,0,0,0.45)",
                mt: 0.25,
              }}
            >
              ID: {team.id}
            </Typography>
          </Box>

          {/* Reactivate button */}
          <Button
            variant="contained"
            size="small"
            disabled={!!activatingId}
            onClick={() => activateTeam(team.id)}
            startIcon={
              activatingId === team.id ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              minHeight: 44,
              minWidth: 120,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              boxShadow: "0 3px 10px rgba(255,144,21,0.25)",
              flexShrink: 0,
              "&:hover": {
                background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)",
                boxShadow: "0 4px 14px rgba(255,144,21,0.3)",
              },
              "&:active": {
                transform: "scale(0.97)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(0,0,0,0.12)",
              },
            }}
          >
            {activatingId === team.id ? "Réactivation..." : "Réactiver"}
          </Button>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
        },
      }}
    >
      {/* Sticky AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          color: "rgba(0,0,0,0.87)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
          },
        }}
      >
        <Toolbar sx={{ minHeight: 56, px: 1 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            sx={{
              minWidth: 44,
              minHeight: 44,
              borderRadius: "12px",
              color: "rgba(0,0,0,0.7)",
              transition: TRANSITION_SMOOTH,
              "&:active": { transform: "scale(0.94)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              flex: 1,
              ml: 0.5,
              letterSpacing: "-0.01em",
            }}
          >
            Équipes inactives
          </Typography>
        </Toolbar>

        {/* Search bar */}
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Recherche..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.35)" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "14px",
                backgroundColor: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(8px)",
                fontSize: "16px",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                transition: TRANSITION_SMOOTH,
                "& fieldset": { borderColor: "rgba(255,144,21,0.08)" },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(255,144,21,0.4)",
                  boxShadow: "0 0 0 3px rgba(255,144,21,0.08)",
                },
              },
            }}
          />
        </Box>
      </AppBar>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pb: 3,
          pt: 1,
          WebkitOverflowScrolling: "touch",
          mt: 0,
        }}
      >
        {filteredTeams.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
              gap: 1.5,
            }}
          >
            <GroupOffOutlinedIcon
              sx={{ fontSize: 48, color: "rgba(0,0,0,0.2)" }}
            />
            <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.95rem" }}>
              Aucune équipe inactive trouvée.
            </Typography>
          </Box>
        ) : (
          filteredTeams.map((team, i) => renderTeamCard(team, i))
        )}
      </Box>
    </Dialog>
  );
}
